import React, { useEffect } from 'react';
import { AssignmentForm } from '../../features/coordinator/components/molecules/AssignmentForm';
import { useAssignmentForm } from '../../features/coordinator/hooks/useAssignmentForm';
import {
  Modal,
  Stack,
  Button,
  Group,
  Alert,
  Loader,
  Box,
  Divider,
  Accordion,
  Text,
  Badge
} from '@mantine/core';
import {
  IconUser,
  IconAlertTriangle,
  IconCheck
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useCreateAssignment } from '../../hooks/api/useAssignments';
import { useAutoSave } from '../../hooks/useAutoSave';
  

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface Doctor {
  id: string;
  name: string;
  email: string;
  specialty?: string;
  department?: string;
  role?: string;
}

interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  isActive: boolean;
}


export interface ConflictSuggestion {
  action: string;
  roomId?: string;
  sessionTemplateId?: string;
  message: string;
}

interface Assignment {
  id: string;
  title: string;
  description?: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';
  date: string;
  room: Room;
  user?: Doctor;
  users?: Doctor;
  doctor?: Doctor;
  sessionTime?: SessionTime;
  session_times?: SessionTime;
}

interface AssignModalProps {
  opened: boolean;
  onClose: () => void;
  room?: Room;
  sessionTime?: SessionTime;
  date?: Date;
  doctors?: Doctor[];
  loading?: boolean;
  conflicts?: string[];
  suggestions?: ConflictSuggestion[];
  // ENHANCED: 08-10-2025 22:30:00 - Hierarchical selection support
  filterOptions?: {
    buildings: Array<{ value: string; label: string }>;
    floors: Array<{ value: string; label: string }>;
    rooms: Array<{ value: string; label: string }>;
    sessionTimes: Array<{ value: string; label: string }>;
    users: Array<{ value: string; label: string }>;
  };
  contextData?: {
    selectedBuilding?: string | null;
    selectedFloor?: string | null;
    selectedRooms?: string[];
    dateRange?: [Date, Date];
  };
  // ENHANCED: 09-10-2025 01:30:00 - Edit mode support
  selectedAssignment?: Assignment | null;
}

/**
 * M1: Assign Modal - Create new assignment with validation and conflict detection
 * 
 * Features:
 * - Doctor selection with search and filtering
 * - Optional note field
 * - Real-time conflict detection
 * - Actionable suggestions for conflict resolution
 * - Proper validation and error handling
 * - Accessibility support
 */
const AssignModal: React.FC<AssignModalProps> = ({
  opened,
  onClose,
  room,
  sessionTime,
  date,
  doctors = [],
  loading = false,
  conflicts = [],
  suggestions = [],
  // ENHANCED: 08-10-2025 22:30:00 - Hierarchical selection props
  filterOptions,
  contextData,
  // ENHANCED: 09-10-2025 01:30:00 - Edit mode support
  selectedAssignment
}) => {
  // =============================================================================
  // HOOKS AND STATE MANAGEMENT
  // =============================================================================
  
  const assignmentForm = useAssignmentForm({
    room,
    sessionTime,
    date,
    doctors,
    filterOptions,
    contextData,
    // ENHANCED: 09-10-2025 01:30:00 - Pass selectedAssignment for edit mode
    selectedAssignment: selectedAssignment || null
  });

  const {
    selectedDoctorId,
    note,
    submitting,
    selectedBuildingId,
    selectedFloorId,
    selectedRoomId,
    selectedSessionTimeId,
    selectedDate,
    availableFloors,
    availableRooms,
    availableSessionTimes,
    filteredDoctors,
    setSubmitting,
    onBuildingChange,
    onFloorChange,
    onRoomChange,
    onSessionTimeChange,
    onDateChange,
    onNoteChange,
    onDoctorChange,
    validateFormInputs,
    createAssignmentData,
    handleAssignmentSuccess,
    handleAssignmentError,
    canSubmit,
    finalRoom,
    finalSessionTime,
    finalDate
  } = assignmentForm;
  
  // =============================================================================
  // API HOOKS
  // =============================================================================
  
  const createAssignmentMutation = useCreateAssignment();
  
  // =============================================================================
  // AUTO-SAVE FUNCTIONALITY
  // =============================================================================
  
  const formData = {
    selectedDoctorId,
    note,
    selectedBuildingId,
    selectedFloorId,
    selectedRoomId,
    selectedSessionTimeId,
    selectedDate: selectedDate ? selectedDate.toISOString() : null
  };
  const { clear: clearAutoSave } = useAutoSave(formData, {
    key: `assign-modal-${room?.id}-${sessionTime?.id}-${date?.toISOString()}`,
    debounceMs: 500,
    ttl: 24 * 60 * 60 * 1000,
    enabled: opened && !!selectedDoctorId
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (opened) {
      // ENHANCED: 08-10-2025 23:10:00 - Clear any old auto-save data that might interfere with date
      clearAutoSave();
      
      // Also manually clear any localStorage entries for this modal
      try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('mchd-autosave-assign-modal-')) {
            localStorage.removeItem(key);
          }
        });
      } catch (error) {
        // Silently handle localStorage errors
      }
    }
  }, [opened, clearAutoSave]);

  // =============================================================================
  // FORM SUBMISSION HANDLER
  // =============================================================================

  /**
   * Main form submission handler
   */
  const handleSubmit = async () => {
    const validation = validateFormInputs();
    if (!validation.isValid) {
      notifications.show({
        title: 'Validation Error',
        message: validation.error,
        color: 'red'
      });
      return;
    }

    setSubmitting(true);
    try {
      const assignmentData = createAssignmentData(finalRoom, finalSessionTime, finalDate!);
      await createAssignmentMutation.mutateAsync(assignmentData);
      clearAutoSave();
      handleAssignmentSuccess();
      onClose();
    } catch (error) {
      handleAssignmentError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <IconUser size={20} />
          <Text fw={600}>Assign Doctor to Room</Text>
        </Group>
      }
      size={600}
      centered
      closeOnClickOutside={false}
      closeOnEscape={!submitting}
      trapFocus
      returnFocus
      styles={{
        content: {
          maxHeight: '90vh',
          overflow: 'auto'
        }
      }}
    >
      <Stack gap="md">
        {/* ENHANCED: 08-10-2025 23:55:00 - Refactored to use AssignmentForm molecule */}
        <AssignmentForm
          selectedBuildingId={selectedBuildingId}
          selectedFloorId={selectedFloorId}
          selectedRoomId={selectedRoomId}
          selectedSessionTimeId={selectedSessionTimeId}
          selectedDate={selectedDate}
          note={note}
          selectedDoctorId={selectedDoctorId}
          room={room}
          sessionTime={sessionTime}
          date={date}
          filterOptions={filterOptions}
          selectedAssignment={selectedAssignment || null}
          onBuildingChange={onBuildingChange}
          onFloorChange={onFloorChange}
          onRoomChange={onRoomChange}
          onSessionTimeChange={onSessionTimeChange}
          onDateChange={onDateChange}
          onNoteChange={onNoteChange}
          onDoctorChange={onDoctorChange}
          availableFloors={availableFloors}
          availableRooms={availableRooms}
          availableSessionTimes={availableSessionTimes}
          filteredDoctors={filteredDoctors}
          loading={loading}
          submitting={submitting}
        />

        <Divider />

        {/* Conflicts Alert */}
        {conflicts.length > 0 && (
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Assignment Conflicts Detected"
            color="red"
            variant="light"
          >
            <Stack gap="xs">
              {conflicts.map((conflict, index) => (
                <Text key={index} size="sm">
                  • {conflict}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <Box>
            <Text size="sm" fw={500} mb="xs">Suggested Alternatives</Text>
            <Stack gap="xs">
              {suggestions.map((suggestion, index) => (
                <Alert
                  key={index}
                  color="blue"
                  variant="light"
                  icon={<IconCheck size={16} />}
                >
                  <Text size="sm">{suggestion.message}</Text>
                </Alert>
              ))}
            </Stack>
          </Box>
        )}

        {/* Conflict Suggestions - Inline Accordion */}
        {conflicts && conflicts.length > 0 && (
          <Alert 
            icon={<IconAlertTriangle size={16} />} 
            title="Conflicts Detected" 
            color="red"
            variant="light"
          >
            <Stack gap="sm">
              <Text size="sm">
                {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} found. Review suggestions below:
              </Text>
              
              {suggestions && suggestions.length > 0 ? (
                <Accordion variant="separated" defaultValue={null}>
                  {suggestions.map((suggestion, index) => (
                    <Accordion.Item key={index} value={`suggestion-${index}`}>
                      <Accordion.Control>
                        <Group gap="xs">
                          <Badge size="sm" color="orange" variant="dot">
                            Suggestion {index + 1}
                          </Badge>
                          <Text size="sm" fw={500}>
                            {suggestion.action}
                          </Text>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack gap="sm">
                          <Text size="sm" c="dimmed">
                            {suggestion.message}
                          </Text>
                          <Button 
                            size="xs" 
                            variant="light" 
                            color="blue"
                            leftSection={<IconCheck size={14} />}
                            onClick={() => {
                              // Apply suggestion logic would go here
                              notifications.show({
                                title: 'Suggestion Applied',
                                message: suggestion.message,
                                color: 'blue'
                              });
                            }}
                          >
                            Apply Suggestion
                          </Button>
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              ) : (
                <Text size="sm" c="dimmed">
                  No automatic suggestions available. Please adjust assignment details manually.
                </Text>
              )}
            </Stack>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box ta="center" py="md">
            <Loader size="sm" />
            <Text size="sm" c="dimmed" mt="xs">
              Validating assignment...
            </Text>
          </Box>
        )}

        {/* Action Buttons */}
        <Group justify="flex-end" gap="md" mt="lg">
          <Button
            variant="light"
            onClick={onClose}
            disabled={submitting}
            size="md"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting || createAssignmentMutation.isPending}
            leftSection={<IconCheck size={16} />}
            size="md"
            style={{
              minWidth: '140px'
            }}
          >
            {(submitting || createAssignmentMutation.isPending) ? 'Creating...' : 'Create Assignment'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default React.memo(AssignModal);
